import React from "react";
import { useForm } from "react-hook-form";
import { Form } from "../../../../../components/ui/common/form";
import { TableCell, TableRow } from "../../../../../components/ui/common/table";
import { FormInput } from "../../../../../components/ui/Input/FormInput/FormInput";
import { FormInputNumber } from "../../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../../components/ui/Input/FormSelector/FormSelect";
import { FormUnitInput } from "../../../../../components/ui/Input/FormUnitInput/FormUnitInput";
import { cn } from "../../../../../lib/utils";
import { MASS_UNIT_OPTIONS, UNIT_LABEL_MAP } from "../../shared/constants/measurementUnits.constants";
import RowActions from "../../shared/components/RowActions";
import { mapFormValuesToRecord, mapRecordToFormValues } from "../utils/processRecords.utils";
import {
  ApiProcessingRecord,
  GateKind,
  ProcessEditFormValues,
  ProcessRecordLookups
} from "../types/processRecords.types";
import ExpandChevron from "./ExpandChevron";
import ProcessExpandedRow from "./ProcessExpandedRow";

interface ProcessEditRowProps {
  process: ApiProcessingRecord;
  expanded: boolean;
  gateKind: GateKind;
  lookups: ProcessRecordLookups;
  onToggleExpand: () => void;
  onSave: (updated: ApiProcessingRecord) => void;
  onCancel: () => void;
  onSaveCipSip: (updated: ApiProcessingRecord) => void;
}

/** Inline editor for one process row. Owns its own FormProvider. */
const ProcessEditRow: React.FC<ProcessEditRowProps> = ({
  process,
  expanded,
  gateKind,
  lookups,
  onToggleExpand,
  onSave,
  onCancel,
  onSaveCipSip
}) => {
  const form = useForm<ProcessEditFormValues>({
    defaultValues: mapRecordToFormValues(process),
    mode: "all"
  });

  const handleSave = form.handleSubmit(values => onSave(mapFormValuesToRecord(values, process)));

  return (
    // Form = FormProvider — renders no DOM element, table structure stays valid
    <Form {...form}>
      <TableRow className={cn(expanded && "tw-bg-accent/30 !tw-overflow-auto")}>
        <TableCell className="tw-p-0 tw-text-center">
          <ExpandChevron expanded={expanded} asButton onToggle={onToggleExpand} />
        </TableCell>

        <TableCell>
          <FormInput
            control={form.control}
            name="processingName"
            label=""
            className="!tw-h-10 tw-text-xs"
          />
        </TableCell>

        <TableCell>
          <FormSelect
            control={form.control}
            name="processingType"
            label=""
            placeholder="Select type"
            options={lookups.processTypeOptions}
          />
        </TableCell>

        <TableCell>
          <FormSelect
            control={form.control}
            name="facilityName"
            label={null}
            placeholder="Select facility"
            options={lookups.facilityOptions.map(f => ({
              key: f.value,
              value: f.value,
              label: f.label
            }))}
          />
        </TableCell>

        <TableCell>
          <FormUnitInput
            control={form.control}
            name="capacity"
            label=""
            unitName="capacityUnit"
            unitOptions={MASS_UNIT_OPTIONS}
          />
        </TableCell>

        <TableCell>
          <FormUnitInput
            control={form.control}
            name="input"
            label=""
            unitName="inputUnit"
            unitOptions={MASS_UNIT_OPTIONS}
          />
        </TableCell>

        <TableCell>
          <FormUnitInput
            control={form.control}
            name="output"
            label=""
            unitName="outputUnit"
            unitOptions={MASS_UNIT_OPTIONS}
          />
        </TableCell>

        <TableCell>
          <FormUnitInput
            control={form.control}
            name="wasteSideStream"
            label=""
            unitName="wasteSideStreamUnit"
            unitOptions={MASS_UNIT_OPTIONS}
          />
        </TableCell>

        <TableCell className="tw-text-right">
          <FormInputNumber
            control={form.control}
            name="electricity"
            label=""
            rightContentEnable
            rightContent={UNIT_LABEL_MAP[process.electricityUnit] ?? ""}
          />
        </TableCell>

        <TableCell className="tw-text-right">
          <FormInputNumber
            control={form.control}
            name="water"
            label=""
            rightContentEnable
            rightContent={UNIT_LABEL_MAP[process.waterUnit] ?? ""}
          />
        </TableCell>

        <TableCell className="tw-text-right">
          <FormInputNumber
            control={form.control}
            name="heat"
            label=""
            rightContentEnable
            rightContent={UNIT_LABEL_MAP[process.heatUnit] ?? ""}
          />
        </TableCell>

        <TableCell className="tw-text-right">
          <RowActions
            editing
            onEdit={() => undefined}
            onSave={handleSave}
            onCancel={onCancel}
            buttonClassName="tw-h-7 tw-w-7"
          />
        </TableCell>
      </TableRow>

      {expanded && (
        <ProcessExpandedRow process={process} gateKind={gateKind} onSaveCipSip={onSaveCipSip} />
      )}
    </Form>
  );
};

export default ProcessEditRow;
